import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { PetDetails } from '@weave/schema-gen-ts/dist/schemas/pets-api/v1/pets_api.pb';
import { CallIntelligenceWritebackV1 } from '@frontend/api-call-intel';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Chip, Text } from '@frontend/design-system';
import { generateTrackingId } from '../../utils';
import { formatSyncDate, getSummarySyncChipConfig, getSyncErrorMessage } from './utils';

interface SummarySyncPopoverContentProps {
  callWritebacks?: CallIntelligenceWritebackV1.Types.CallWritebackStatus[];
  summarySyncMode?: CallIntelligenceWritebackV1.Types.WritebackSettingMode;
  onRetrySync?: (serviceablePmsIdList?: string[]) => void;
  isSyncing?: boolean;
  isDemo?: boolean;
  hasManualSyncAccess?: boolean;
  petPmsIdToDetailsMap?: Record<string, PetDetails>;
  openSyncPetsModal?: () => void;
  hasUnsyncedPets?: boolean;
}

const COMPONENT_ID = 'summary-sync-popover';

const VISIBLE_WRITEBACK_STATUSES = [
  CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_SUCCEEDED,
  CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED,
  CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_INPROGRESS,
];

export const SummarySyncPopoverContent: FC<SummarySyncPopoverContentProps> = ({
  callWritebacks,
  isSyncing,
  isDemo,
  summarySyncMode,
  hasManualSyncAccess,
  petPmsIdToDetailsMap,
  hasUnsyncedPets,
  onRetrySync,
  openSyncPetsModal,
}) => {
  const { t } = useTranslation('call-intelligence');

  const syncConfigList = useMemo(() => {
    // For non-vet vertical, show only single writeback status
    return callWritebacks
      ?.filter(({ status }) => !!status && VISIBLE_WRITEBACK_STATUSES.includes(status))
      .map((writebackStatus) => ({
        ...writebackStatus,
        syncError: getSyncErrorMessage({
          writebackStatusResponse: writebackStatus,
          summarySyncMode,
        }),
        petName: writebackStatus.serviceablePmsId
          ? petPmsIdToDetailsMap?.[writebackStatus.serviceablePmsId]?.name
          : undefined,
        chipConfig: getSummarySyncChipConfig({
          writebackStatus,
        }),
      }));
  }, [summarySyncMode, callWritebacks, petPmsIdToDetailsMap]);

  const { failedSyncPmsServiceableIdList, hasAnyFailedSync } = useMemo(() => {
    const failedWritebacks =
      callWritebacks?.filter(
        (writebackStatus) =>
          writebackStatus.status === CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED
      ) || [];
    return {
      hasAnyFailedSync: failedWritebacks.length > 0,
      failedSyncPmsServiceableIdList: failedWritebacks
        .map((writebackStatus) => writebackStatus.serviceablePmsId || '')
        .filter(Boolean),
    };
  }, [callWritebacks]);

  return (
    <div css={styles.container} data-testid='summary-sync-popover-content'>
      <div css={styles.body}>
        <Text weight='bold'>{t('Call Summary')}</Text>
        {syncConfigList?.map((syncData) => (
          <div css={styles.syncItem} key={syncData.serviceablePmsId}>
            <div css={styles.syncItemRow} key={syncData.serviceablePmsId}>
              <Chip
                variant={syncData.chipConfig.chipVariant}
                css={css`
                  color: ${syncData.chipConfig.color};
                `}
              >
                {syncData.chipConfig.defaultLabel ?? t('Unknown')}
              </Chip>
              {syncData.petName && (
                <Text size='small' color='subdued' weight='bold'>
                  {syncData.petName} •
                </Text>
              )}
              {syncData?.lastSyncedAt && (
                <Text size='small' color='light'>
                  {formatSyncDate(syncData.lastSyncedAt)}
                </Text>
              )}
            </div>

            {syncData?.syncError && (
              <Text size='small' color='error'>
                {syncData.syncError}
              </Text>
            )}
          </div>
        ))}
      </div>
      {hasManualSyncAccess && hasAnyFailedSync && (
        <>
          <Divider margin={0} />
          <div css={styles.action}>
            <Button
              variant='tertiary'
              iconName='data-sync-small'
              css={styles.actionButton}
              onClick={() => onRetrySync?.(failedSyncPmsServiceableIdList)}
              disabled={isSyncing}
              trackingId={generateTrackingId({
                component: COMPONENT_ID,
                context: 'retry-sync-button',
                isDemoAccount: isDemo,
              })}
            >
              {t('Retry Sync')}
            </Button>
          </div>
        </>
      )}
      {hasManualSyncAccess && hasUnsyncedPets && (
        <>
          <Divider margin={0} />
          <div css={styles.action}>
            <Button
              variant='tertiary'
              iconName='vet-dog-small'
              css={styles.actionButton}
              onClick={openSyncPetsModal}
              trackingId={generateTrackingId({
                component: COMPONENT_ID,
                context: 'sync-additional-pets-button',
                isDemoAccount: isDemo,
              })}
            >
              {t('Sync Additional Pets')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
  `,
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(2)};
  `,
  syncItem: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  `,
  syncItemRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  action: css`
    padding: ${theme.spacing(1, 2)};
    display: flex;
    align-items: center;
  `,
  actionButton: css`
    color: ${theme.colors.neutral50};
    svg {
      color: ${theme.colors.neutral50};
    }
  `,
};
